import type { MeFieldsFragment, MyIdentityFieldsFragment } from '@mbfab/client-api'
import { api } from '@mbfab/client-api'
import { isNull, isSome } from '@mbfab/shared'
import { createFileRoute, Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { ArrowLeft, LogOut } from 'lucide-react'
import platform from 'platform'
import { useEffect } from 'react'

import { CenteredLoading } from '@/components/app/centered-loading'
import { GraphqlErrorPanel } from '@/components/app/graphql-error-panel'
import { ErrorPanel } from '@/components/ui/error-panel'
import { classifyGraphqlError } from '@/lib/apollo/error-classification'
import { setLastTenantId } from '@/lib/last-tenant'
import { Posthog } from '@/lib/monitoring/posthog'
import { setMonitoringUserInfo } from '@/lib/monitoring/user-info'
import { canAccessTenantApp, isManagerOnlyPath, isTenantManager } from '@/lib/tenant-access'
import { AuthenticatedApolloProvider } from '@/providers/authenticated-apollo-provider'
import { MeProvider } from '@/providers/me-context'

// v1 MemberLayout の isOnboardingPage と同じ。ガードの除外先とサイドバー非表示対象を一致させる
function isOnboardingPage(pathname: string, tenantId: string): boolean {
  return (
    pathname === `/t/${tenantId}/terms_of_use` ||
    pathname === `/t/${tenantId}/plan/confirm` ||
    pathname === `/t/${tenantId}/plan/complete`
  )
}

export const Route = createFileRoute('/t/$tenant_id')({
  component: Layout,
})

function Layout() {
  const { tenant_id: tenantId } = Route.useParams()

  return (
    <AuthenticatedApolloProvider tenantId={tenantId}>
      <MeGate />
    </AuthenticatedApolloProvider>
  )
}

function MeGate() {
  const navigate = useNavigate()
  const meQuery = api.useFetchMeQuery()
  // myIdentity はサイドバーのメニュー表示制御（admin ロール / 所属テナント数）に使う。
  // v1 の Sider も両方を引いており、ゲートで一度だけ引いて MeContext 経由で配る
  const identityQuery = api.useFetchMyIdentityQuery()

  const me = meQuery.data?.me
  const myIdentity = identityQuery.data?.myIdentity
  const hasData = isSome(me) && isSome(myIdentity)

  // 全画面ローディングは「まだ一度も取得できていない」ときだけにする。
  // refetch 中も出してしまうと配下の画面が丸ごとアンマウントされ、入力途中の内容や
  // 操作結果バナーが消える（組織設定の保存は refetchQueries で FetchMe を引き直すため、
  // 実際に保存成功バナーが表示されない不具合になっていた。
  // Apollo Client 4 は notifyOnNetworkStatusChange が既定 true で refetch ごとに loading が立つ）
  if (!hasData && (meQuery.loading || identityQuery.loading)) {
    return <CenteredLoading message="読み込み中..." />
  }

  const queryError = meQuery.error ?? identityQuery.error
  if (isSome(queryError)) {
    const classification = classifyGraphqlError(queryError)

    // 認証切れだけは hasData に関係なく必ず出す。
    // Apollo は同一クエリ・同一変数の refetch が失敗しても直前の data を保持するため、
    // hasData で抑制すると「一度でも表示できた後はセッションが切れても何も出ない」ことになり、
    // 画面は正常に見えるのに全操作が失敗し続ける無言状態になる。
    // このクライアントはマウント時のトークンを固定で持つので自動復帰もしない（再ログインが唯一の復帰手段）
    if (classification === 'unauthenticated') {
      return <GraphqlErrorPanel classification="unauthenticated" subject="テナント" />
    }

    // 以降（権限エラー・その他）は、一度でも表示できているなら全画面エラーに差し替えない。
    // stale なデータのまま使い続けられる方が、操作結果ごと画面が吹き飛ぶより害が小さい
    if (isSome(me) && isSome(myIdentity)) {
      return <MeResolved me={me} myIdentity={myIdentity} />
    }

    // 「このテナントにはアクセスできません」は fetchMe（テナントスコープ）の拒否にしか当てはまらない。
    // fetchMyIdentity はテナント非依存なので、そちらの forbidden は下の汎用エラーに落とす
    if (classification === 'forbidden' && isSome(meQuery.error)) {
      return (
        <ErrorPanel
          title="このテナントにはアクセスできません"
          description="別のアカウントでログインするか、テナント選択に戻ってください。"
          action={{
            label: 'テナント選択に戻る',
            icon: ArrowLeft,
            onClick: () => {
              void navigate({ to: '/' })
            },
          }}
        />
      )
    }

    return (
      <GraphqlErrorPanel
        classification={classification}
        subject="テナント情報"
        action={{
          label: 'テナント選択に戻る',
          icon: ArrowLeft,
          onClick: () => {
            void navigate({ to: '/' })
          },
        }}
      />
    )
  }

  if (isNull(me) || isNull(myIdentity)) {
    return <CenteredLoading message="読み込み中..." />
  }

  return <MeResolved me={me} myIdentity={myIdentity} />
}

function MeResolved({ me, myIdentity }: { me: MeFieldsFragment; myIdentity: MyIdentityFieldsFragment }) {
  const navigate = useNavigate()

  if (!canAccessTenantApp(me)) {
    // いまテナントに入れないのは「利用開始前（preActive）のテナントの member」だけ。
    // 同意フローは manager / admin が行うため、member には待つべき理由を示す
    return (
      <ErrorPanel
        title="利用開始の手続き中です"
        description="このテナントはまだ利用を開始していません。組織の管理者が利用規約とプランに同意すると使えるようになります。"
        action={{
          label: 'ログアウト',
          icon: LogOut,
          onClick: () => {
            void navigate({ to: '/sign_out' })
          },
        }}
      />
    )
  }

  return <AuthorizedMeResolved me={me} myIdentity={myIdentity} />
}

function AuthorizedMeResolved({ me, myIdentity }: { me: MeFieldsFragment; myIdentity: MyIdentityFieldsFragment }) {
  const navigate = useNavigate()
  const pathname = useLocation({ select: (location) => location.pathname })
  const trackingProperties = {
    name: `${me.name} / ${me.tenant.name}`,
    email: me.email || 'unknown@buffup.jp',
    tenantId: me.tenant.id,
    tenantName: me.tenant.name,
    userId: me.id,
    userName: me.name,
    role: me.role,
    propertyConfiguredAt: dayjs().format('YYYY-MM-DD'),
    platform: platform.toString(),
  }

  useEffect(() => {
    setMonitoringUserInfo(me)
    Posthog.identify(me.id, trackingProperties)
  }, [me.id, me.email, me.name, me.role, me.tenant.id, me.tenant.name])

  useEffect(() => {
    Posthog.trackAccess(pathname.replace(/\/t\/[^/]+/, '/t/[tenant_id]'), trackingProperties)
  }, [pathname, me.id, me.tenant.id])

  // 「前回利用」テナントの書き込みはここで一元化する（テナント切替画面のクリックだけでなく、
  // 所属1件の自動遷移・ブックマーク直行でも更新されるようにするため）
  useEffect(() => {
    setLastTenantId(me.tenant.id)
  }, [me.tenant.id])

  // preActive テナントを利用規約同意フローへ誘導する（v1 MemberLayout と同じ条件）。
  // navigate はレンダー中に呼ぶと警告になるため useEffect で副作用として実行する。
  // admin 新規テナントは現状 status=active 作成の暫定があるが、preActive 運用ではこのガードが必須
  const shouldRedirectToOnboarding =
    isTenantManager(me.role) && me.tenant.status === 'preActive' && !isOnboardingPage(pathname, me.tenant.id)

  useEffect(() => {
    if (shouldRedirectToOnboarding) {
      void navigate({
        to: '/t/$tenant_id/terms_of_use',
        params: { tenant_id: me.tenant.id },
      })
    }
  }, [shouldRedirectToOnboarding, navigate, me.tenant.id])

  if (shouldRedirectToOnboarding) {
    return <CenteredLoading message="読み込み中..." />
  }

  // member が開けないのは、組織とユーザーアカウントの設定の画面（ユーザー・組織設定）だけ。
  // メニューには出さないが、URL 直打ち・ブックマークで開いたときに読み込みエラーではなく理由を示す
  if (isManagerOnlyPath(pathname, me.tenant.id, me.role)) {
    return (
      <ErrorPanel
        title="権限がありません"
        description="この画面は「管理者」ロールのユーザーだけが使えます。必要な場合は組織の管理者にご依頼ください。"
        action={{
          label: 'ミルシートへ戻る',
          icon: ArrowLeft,
          onClick: () => {
            void navigate({ to: '/t/$tenant_id', params: { tenant_id: me.tenant.id } })
          },
        }}
      />
    )
  }

  return (
    <MeProvider me={me} myIdentity={myIdentity}>
      <Outlet />
    </MeProvider>
  )
}
